/* eslint-disable */
// @ts-nocheck
import {
    ARROW_LOCATION_PROPTYPE,
    STYLE_PROPTYPE,
    Tooltip,
    TOOLTIP_CONTENT_PROPTYPE,
} from '@eventbrite/eds-tooltip';
import { getAdditionalProps } from '@eventbrite/eds-utils';
import classNames from 'classnames';
// original author: vlin
import PropTypes from 'prop-types';
import React, { PureComponent, useEffect, useRef } from 'react';

/**
 * withTooltip is an HOC that provides the wrappedComponent with a tooltip.
 *
 * To use the HOC, wrap the component like so:
 *   let ButtonWithTooltip = withTooltip(Button);
 *
 * Then, provide the following props through the wrappedComponent
 *
 * e.g. <ButtonWithTooltip tooltipText="A button"> This is Button Text </ButtonWithTooltip>
 * 1) tooltipText             - required
 * 2) arrowLocation           - optional
 * 3) tooltipStyle            - optional
 * 4) hideTooltipOnMouseLeave - optional
 * 5) keepTooltipOpen         - optional
 *
 * @summary An HOC that provides the wrappedComponent with an accompanying tooltip
 */
const withTooltip = (Component) =>
    class WithTooltipWrapper extends PureComponent {
        static propTypes = {
            /**
             * The text to be set as id for tooltip
             **/
            tooltipId: PropTypes.string.isRequired,

            /**
             * The text or React element to be displayed.
             * If text is provided, it must be a label, no sentences, with a max character count of 32.
             **/
            tooltipText: TOOLTIP_CONTENT_PROPTYPE.isRequired,

            /**
             * Location of arrow for tooltip relative to targetNode. The direction chosen
             * relates to which side of the Tooltip the arrow will appear.
             * Options: left, right, top, bottom
             **/
            tooltipArrowLocation: ARROW_LOCATION_PROPTYPE,

            /**
             * The background color of the Tooltip. Options: basic, phab
             **/
            tooltipStyle: STYLE_PROPTYPE,

            /**
             * If true, the tooltip will hide anytime the mouse leaves the
             * targetNode whether the targetNode has focus or not.
             **/
            hideTooltipOnMouseLeave: PropTypes.bool,

            /**
             * If true, the tooltip will NOT hide anytime the mouse leaves the
             * targetNode whether the targetNode has focus or not.
             **/
            keepTooltipOpen: PropTypes.bool,

            /**
             * Wrapper component tag. Defaults to DIV
             **/
            wrapperComponent: PropTypes.string,

            __containerClassName: PropTypes.string,
        };

        state = {
            targetNode: null,
            element: null,
        };

        show = () => {
            this._handleShow({ currentTarget: this.state.element });
        };

        _handleShow = (e) => {
            this.setState({
                targetNode: e.currentTarget,
            });
        };

        hide = () => {
            this._handleHide();
        };

        _handleHide = () => {
            this.setState({
                targetNode: null,
            });
        };

        render() {
            const { targetNode } = this.state;
            const {
                tooltipId,
                tooltipText,
                tooltipArrowLocation,
                tooltipStyle,
                __containerClassName,
            } = this.props;
            const componentProps = getAdditionalProps(this);
            const tooltipClasses = classNames(
                'eds-tooltip--hoc-wrapper',
                __containerClassName,
            );
            let describedBy;

            // We don't want `aria-describedby` on the div when no tooltip is
            // present
            if (targetNode) {
                describedBy = tooltipId;
            }

            return (
                <ToolTipWrapper
                    tooltipClasses={tooltipClasses}
                    describedBy={describedBy}
                    onElementReady={(element) => {
                        this.setState({
                            element,
                        });
                    }}
                >
                    <Component {...componentProps} />
                    <Tooltip
                        tooltipId={tooltipId}
                        targetNode={targetNode}
                        arrowLocation={tooltipArrowLocation}
                        style={tooltipStyle}
                    >
                        {tooltipText}
                    </Tooltip>
                </ToolTipWrapper>
            );
        }
    };

const ToolTipWrapper = ({
    tooltipClasses,
    describedBy,
    children,
    onElementReady,
}) => {
    const element = useRef(null);

    useEffect(() => {
        let unmounted = false;

        if (element.current && !unmounted) {
            onElementReady(element.current);
        }

        return () => {
            unmounted = true;
        };
        // eslint-disable-next-line
    }, [element.current, onElementReady]);

    return (
        /* Accessibility: This component does have key events, but not picked up by the linter becasue of the HOC */
        /* Ideally this component would be a button element but as it is a HOC, that has already been relased we can't do this. e.g. copy button with tooltip */
        /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
        <div
            ref={element}
            className={tooltipClasses}
            // `aria-describedby` is based on the <button> tag and not
            // with the component because in cases where component has
            // `aria-hidden` (i.e. <i> ), we still want `aria-describedby`
            // to be present and corresponds to the tooltip id
            aria-describedby={describedBy}
            data-spec="tooltip-wrapper"
            tabIndex="0"
        >
            {children}
        </div>
    );
};

export default withTooltip;
